import { useCallback, useEffect, useRef, useState } from 'react';
import { EMPTY_LIBRARY, parseLibrary, toPlainText, } from '@orbit-mind/domain';
export function useLibraryOrganization(store) {
    const [library, setLibrary] = useState(EMPTY_LIBRARY);
    const current = useRef(EMPTY_LIBRARY);
    const [ready, setReady] = useState(false);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const mounted = useRef(true);
    const writing = useRef(false);
    const [version, setVersion] = useState(0);
    useEffect(() => {
        mounted.current = true;
        void store
            .read()
            .then((value) => {
            const parsed = value === undefined ? EMPTY_LIBRARY : parseLibrary(JSON.parse(value));
            if (mounted.current) {
                current.current = parsed;
                setLibrary(parsed);
                setReady(true);
            }
        })
            .catch(() => {
            if (mounted.current)
                setError('Não foi possível ler a organização da biblioteca. Seus mapas continuam disponíveis.');
        });
        return () => {
            mounted.current = false;
        };
    }, [store, version]);
    const reload = useCallback(() => {
        if (!writing.current)
            setVersion((value) => value + 1);
    }, []);
    async function update(change) {
        if (!ready || writing.current)
            return false;
        writing.current = true;
        setBusy(true);
        setError('');
        try {
            // On top of what is stored, not of what this screen read: a sync from another device or a
            // map filed from another screen may have written since, and must not be written over.
            const stored = await store.read();
            const latest = stored === undefined ? current.current : parseLibrary(JSON.parse(stored));
            const next = change(latest);
            await store.write(JSON.stringify(next));
            if (mounted.current) {
                current.current = next;
                setLibrary(next);
            }
            return true;
        }
        catch (cause) {
            if (mounted.current)
                setError(cause instanceof Error ? cause.message : 'Não foi possível salvar.');
            return false;
        }
        finally {
            writing.current = false;
            if (mounted.current)
                setBusy(false);
        }
    }
    return { library, ready, busy, error, update, reload };
}
export function useLibraryIndex(maps, read, enabled) {
    const [index, setIndex] = useState({});
    const cache = useRef(index);
    const [indexing, setIndexing] = useState(false);
    useEffect(() => {
        if (!enabled)
            return;
        let cancelled = false;
        const isCancelled = () => cancelled;
        const run = async () => {
            setIndexing(true);
            for (const { map } of maps) {
                if (isCancelled())
                    return;
                if (cache.current[map.id]?.version === map.updatedAt)
                    continue;
                try {
                    const document = await read(map.id);
                    if (isCancelled())
                        return;
                    const entry = {
                        version: map.updatedAt,
                        text: Object.values(document.nodes)
                            .map((node) => `${node.plainText} ${node.notes === undefined ? '' : toPlainText(node.notes)}`)
                            .join('\n')
                            .toLocaleLowerCase(),
                        bytes: new TextEncoder().encode(JSON.stringify(document)).byteLength,
                    };
                    cache.current = { ...cache.current, [map.id]: entry };
                    setIndex(cache.current);
                    await new Promise((resolve) => setTimeout(resolve, 0));
                }
                catch {
                    /* The editor offers recovery for damaged documents. Keep its title searchable. */
                }
            }
            if (!cancelled)
                setIndexing(false);
        };
        void run();
        return () => {
            cancelled = true;
        };
    }, [maps, read, enabled]);
    return { index, indexing: enabled && indexing };
}
//# sourceMappingURL=library.js.map